import {
  Avatar,
  AvatarGroup,
  Box,
  Center,
  HStack,
  Highlight,
  Icon,
  IconButton,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import i18next, { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaLongArrowAltRight, FaRegEye, FaRegEyeSlash, FaTimes } from 'react-icons/fa';
import { FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi';

import type { IndicatorResponsiblePublic } from '@/client';
import { CTag } from '@/components/common/CTag';
import { Tooltip } from '@/components/ui/tooltip';

export const collectionFrequency = createListCollection({
  items: [
    {
      label: i18next.t('indicator.selectValues.collectionFrequency.year'),
      value: 'year',
    },
    {
      label: i18next.t('indicator.selectValues.collectionFrequency.semester'),
      value: 'semester',
    },
    {
      label: i18next.t('indicator.selectValues.collectionFrequency.trimester'),
      value: 'trimester',
    },
  ],
  itemToString: (item) => item.label,
  itemToValue: (item) => item.value,
});

export const trajectory = createListCollection({
  items: [
    {
      label: i18next.t('indicator.selectValues.trajectory.increase'),
      value: 'increase',
    },
    {
      label: i18next.t('indicator.selectValues.trajectory.decrease'),
      value: 'decrease',
    },
    {
      label: i18next.t('indicator.selectValues.trajectory.steady'),
      value: 'steady',
    },
  ],
  itemToString: (item) => item.label,
  itemToValue: (item) => item.value,
});

export const category = createListCollection({
  items: [
    {
      label: i18next.t('indicator.selectValues.category.simple'),
      value: 'simple',
    },
    {
      label: i18next.t('indicator.selectValues.category.qualitative'),
      value: 'qualitative',
    },
    {
      label: i18next.t('indicator.selectValues.category.composite'),
      value: 'composite',
    },
  ],
  itemToString: (item) => item.label,
  itemToValue: (item) => item.value,
});

export const defaultColumnVisibility = {
  pillar_name: true,
  commitment_name: true,
  name: true,
  description: false,
  department: false,
  category: true,
  unit: true,
  confidential: false,
  responsibles: true,
  referentials: false,
  sdgs: false,
};

export const columnsDef = [
  {
    accessorKey: 'name',
    baseKey: 'name',
    header: () => i18next.t('indicator.columnsHeader.indicatorName'),
    size: 200,
    maxSize: 200,
    options: {
      enableSorting: true,
    },
    cell: ({ row }: { row: any }) => {
      const search = new URLSearchParams(location.search).get('search') || '';
      const searchTerms = search.split(' ');

      return (
        <Text fontWeight={'600'} display={'block'}>
          <Highlight query={searchTerms} ignoreCase styles={{ px: '0.5', bg: 'orange.subtle', color: 'orange.fg' }}>
            {row.original.name}
          </Highlight>
        </Text>
      );
    },
  },
  {
    accessorKey: 'unit',
    baseKey: 'unit',
    header: () => i18next.t('indicator.columnsHeader.unit'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      const search = new URLSearchParams(location.search).get('search') || '';
      const searchTerms = search.split(' ');

      return row.original.unit ? (
        <Text display={'block'}>
          <Highlight query={searchTerms} ignoreCase styles={{ px: '0.5', bg: 'orange.subtle', color: 'orange.fg' }}>
            {row.original.unit}
          </Highlight>
        </Text>
      ) : (
        <Text color="gray.500" textStyle="sm">
          N/R
        </Text>
      );
    },
  },
  {
    accessorKey: 'pillar_name',
    baseKey: 'commitment___pillar___name',
    header: () => i18next.t('indicator.columnsHeader.pillarName'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      return (
        <>
          {row.original.pillar_name ? (
            <CTag label={row.original.pillar_name ? row.original.pillar_name : 'N/R'} labelToColor={true} />
          ) : (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          )}
        </>
      );
    },
  },
  {
    accessorKey: 'commitment_name',
    baseKey: 'commitment___name',
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    header: () => i18next.t('indicator.columnsHeader.commitmentName'),
    cell: ({ row }: { row: any }) => {
      const search = new URLSearchParams(location.search).get('search') || '';
      const searchTerms = search.split(' ');

      return row.original.commitment_name ? (
        <Text display={'block'}>
          <Highlight query={searchTerms} ignoreCase styles={{ px: '0.5', bg: 'orange.subtle', color: 'orange.fg' }}>
            {row.original.commitment_name}
          </Highlight>
        </Text>
      ) : (
        <Text color="gray.500" textStyle="sm">
          N/R
        </Text>
      );
    },
  },
  {
    accessorKey: 'category',
    baseKey: 'category',
    header: () => i18next.t('indicator.columnsHeader.categoryName'),
    size: 80,
    minSize: 80,
    maxSize: 80,
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      const isComposite = row.original.category === 'composite';
      const asSimpleIndicator = row.original.composite_as_simple_indicator;

      return (
        <CTag
          label={
            (category.items.find((item) => item.value === row.original.category)?.label || '') +
            (isComposite && asSimpleIndicator ? ' (as simple)' : '')
          }
          bg={'primary.50'}
        />
      );
    },
  },
  {
    accessorKey: 'description',
    baseKey: 'description',
    header: () => i18next.t('indicator.columnsHeader.description'),
    options: {
      enableHiding: true,
    },
    cell: ({ row }: { row: any }) => {
      return row.original.description ? (
        <Text className="ql-editor ql-viewer" lineClamp="5" as="div">
          {parse(DOMPurify.sanitize(row.original.description))}
        </Text>
      ) : (
        <Text color="gray.500" textStyle="sm">
          N/R
        </Text>
      );
    },
  },
  {
    accessorKey: 'department',
    baseKey: 'department',
    header: () => i18next.t('indicator.columnsHeader.department'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      return (
        row.original.department || (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        )
      );
    },
  },
  {
    accessorKey: 'sdgs',
    baseKey: 'sdgs___code',
    header: () => i18next.t('indicator.columnsHeader.sdgs'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      const { i18n } = useTranslation();

      return (
        <HStack wrap={'wrap'}>
          {row.original.sdgs.map((sdg: any) => (
            <Box key={sdg.code}>
              <Avatar.Root shape="rounded" size="md" mr={2}>
                <Avatar.Image alt={sdg.name} src={`/assets/images/sdgs/${i18n.language}/sdg_${sdg.code}.png`} />
              </Avatar.Root>
            </Box>
          ))}
        </HStack>
      );
    },
  },
  {
    accessorKey: 'referentials',
    baseKey: 'referentials___global_referential___name',
    header: () => i18next.t('indicator.columnsHeader.referentials'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      return (
        <VStack alignItems={'start'}>
          {row.original.referentials.map((referential: any) => (
            <VStack gap={0} alignItems={'start'} key={referential.id}>
              {referential.global_referential && (
                <Text color="gray.900" textStyle="sm">
                  {referential.global_referential.name}
                </Text>
              )}
              {referential.global_referential_category && (
                <Text color="gray.700" textStyle="xs">
                  - {referential.global_referential_category.name}
                </Text>
              )}
              {referential.global_referential_sub_category && (
                <Text color="gray.500" textStyle="xs">
                  -- {referential.global_referential_sub_category.name}
                </Text>
              )}
            </VStack>
          ))}
        </VStack>
      );
    },
  },
  {
    accessorKey: 'responsibles',
    baseKey: 'responsibles___first_name',
    header: () => i18next.t('indicator.columnsHeader.responsibles'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      const colorPalette = ['gray', 'red', 'green', 'blue', 'teal', 'pink', 'purple', 'cyan', 'orange', 'yellow'];
      const pickPalette = (name: string) => {
        const index = name.charCodeAt(0) % colorPalette.length;
        return `${colorPalette[index]}.400`;
      };

      return (
        <AvatarGroup gap="0" spaceX="-2" size="xs" stacking="first-on-top" maxW="100px">
          {row.original.responsibles.slice(0, 5).map((responsible: any, index: number) => {
            const full_name = `${responsible.first_name} ${responsible.last_name}`;
            return (
              <Tooltip
                showArrow
                key={index}
                ids={{ trigger: row.original.id + index }}
                content={full_name}
                openDelay={100}
                closeDelay={100}
                contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
              >
                <Avatar.Root ids={{ root: row.original.id + index }} bg={pickPalette(full_name)}>
                  <Avatar.Fallback name={full_name} />
                </Avatar.Root>
              </Tooltip>
            );
          })}
          {row.original.responsibles.length - 5 > 0 && (
            <Tooltip
              showArrow
              ids={{ trigger: 'more' }}
              content={`${row.original.responsibles
                .slice(5)
                .reduce(
                  (acc: string, curr: IndicatorResponsiblePublic, index: number) =>
                    `${acc + (index === 0 ? '' : ', ') + curr.first_name} ${curr.last_name}`,
                  ''
                )}`}
              openDelay={100}
              closeDelay={100}
              contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
            >
              <Avatar.Root ids={{ root: 'more' }}>
                <Avatar.Fallback>+{row.original.responsibles.length - 5}</Avatar.Fallback>
              </Avatar.Root>
            </Tooltip>
          )}
        </AvatarGroup>
      );
    },
  },
  {
    accessorKey: 'confidential',
    baseKey: 'confidential',
    header: () => i18next.t('indicator.columnsHeader.confidential'),
    options: {
      enableSorting: true,
      enableHiding: true,
    },
    cell: ({ row }: { row: any }) => {
      return (
        <Center>
          <CTag
            label={
              !row.original.confidential ? (
                <HStack>
                  <FaRegEye />
                  Oui
                </HStack>
              ) : (
                <HStack>
                  <FaRegEyeSlash />
                  Non
                </HStack>
              )
            }
            bg={!row.original.confidential ? 'primary.100' : 'red.500'}
            color={!row.original.confidential ? 'primary.900' : 'white'}
            pr={2}
          />
        </Center>
      );
    },
  },
];

export const defaultColumnVisibilityManageYear = {
  pillar_name: true,
  commitment_name: true,
  indicator_name: true,
  description: false,
  department: false,
  category: true,
  unit: true,
  trajectory: false,
  objective: true,
  consolidated: false,
};

export const columnsDefManageYear = [
  {
    accessorKey: 'indicator_name',
    baseKey: 'name',
    header: t('collection.columnsHeader.indicatorName'),
    size: 300,
    options: {
      enableSorting: true,
    },
    cell: ({ row }: { row: any }) => (
      <HStack>
        <IconButton
          variant="ghost"
          aria-label="info"
          size="2xs"
          colorPalette="gray"
          borderRadius={'full'}
          color={'gray.500'}
          _hover={{ color: 'white' }}
        >
          <HiOutlineInformationCircle />
        </IconButton>
        {row.original.reference_year === true && (
          <Tooltip
            showArrow
            content={t('collection.manage.columnsHeader.referenceYear')}
            contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
            openDelay={100}
            closeDelay={100}
          >
            <span style={{ cursor: 'pointer' }}>✨</span>
          </Tooltip>
        )}
        <Text fontWeight={'600'}>{row.original.indicator_name}</Text>
      </HStack>
    ),
  },
  {
    accessorKey: 'pillar_name',
    baseKey: 'indicator___commitment___pillar___name',
    header: t('collection.columnsHeader.pillarName'),
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) =>
      row.original.pillar_name ? (
        <CTag label={row.original.pillar_name ? row.original.pillar_name : 'N/R'} labelToColor={true} />
      ) : (
        <Text color="gray.500" textStyle="sm">
          N/R
        </Text>
      ),
  },
  {
    accessorKey: 'commitment_name',
    baseKey: 'indicator___commitment___name',
    header: t('collection.columnsHeader.commitmentName'),
    options: {
      enableSorting: true,
      enableHiding: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      return (
        row.original.commitment_name || (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        )
      );
    },
  },
  {
    accessorKey: 'category',
    baseKey: 'category',
    header: t('collection.columnsHeader.categoryName'),
    size: 80,
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      const isComposite = row.original.category === 'composite';
      const asSimpleIndicator = row.original.composite_as_simple_indicator;

      return (
        <CTag
          label={
            (category.items.find((item) => item.value === row.original.category)?.label || '') +
            (isComposite && asSimpleIndicator ? ' (as simple)' : '')
          }
          bg={'primary.50'}
        />
      );
    },
  },
  {
    accessorKey: 'unit',
    baseKey: 'unit',
    header: t('collection.columnsHeader.unit'),
    size: 80,
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
  },
  {
    accessorKey: 'data_collection_frequency',
    baseKey: 'indicator_year_setting___data_collection_frequency',
    header: t('collection.columnsHeader.collectionFrequency'),
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      const dataCollectionFrequency = collectionFrequency.items.find(
        (item) => item.value === row.original.data_collection_frequency
      );

      return (
        dataCollectionFrequency?.label || (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        )
      );
    },
  },
  {
    accessorKey: 'trajectory',
    baseKey: 'indicator_year_setting___trajectory',
    header: t('collection.columnsHeader.trajectory'),
    size: 80,
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      if (row.original.trajectory === null)
        return (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        );
      const label = trajectory.items.find((item) => item.value === row.original.trajectory)?.label;

      return (
        <>
          {row.original.trajectory === 'decrease' && (
            <HStack color="orange.500">
              <Icon size="md">
                <FaArrowTrendDown />
              </Icon>
              {label}
            </HStack>
          )}
          {row.original.trajectory === 'steady' && (
            <HStack color="yellow.500">
              <Icon size="md">
                <FaLongArrowAltRight />
              </Icon>
              {label}
            </HStack>
          )}
          {row.original.trajectory === 'increase' && (
            <HStack color="green.500">
              <Icon size="md">
                <FaArrowTrendUp />
              </Icon>
              {label}
            </HStack>
          )}
        </>
      );
    },
  },
  {
    accessorKey: 'objective',
    baseKey: 'indicator_year_setting___objective',
    header: t('collection.columnsHeader.objective'),
    size: 100,
    cell: ({ row }: { row: any }) => {
      return (
        row.original.objective?.toString() || (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        )
      );
    },
  },
  {
    accessorKey: 'consolidated',
    baseKey: 'indicator_year_setting___consolidated',
    header: t('collection.columnsHeader.collectedByEntity'),
    size: 140,
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
    cell: ({ row }: { row: any }) => {
      if (row.original.data_collection_frequency == null) {
        return (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        );
      }
      return (
        <Center>
          <CTag
            label={
              !row.original.consolidated ? (
                <HStack>
                  <FaCheck />
                  Oui
                </HStack>
              ) : (
                <HStack>
                  <FaTimes />
                  Non
                </HStack>
              )
            }
            bg={!row.original.consolidated ? 'primary.100' : 'red.500'}
            color={!row.original.consolidated ? 'primary.900' : 'white'}
          />
        </Center>
      );
    },
  },
];

export const columnsSelectCollecte = [
  {
    accessorKey: 'indicator_name',
    baseKey: 'indicator_year_setting___indicator___name',
    header: 'Intitulé des années',
    size: 200,
    options: {
      enableSorting: true,
    },
  },
  {
    accessorKey: 'category',
    baseKey: 'indicator_year_setting___indicator___category',
    header: 'Periodes',
    size: 160,
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
  },
  {
    accessorKey: 'indicator_count',
    baseKey: 'indicator_year_setting___indicator___indicator_count',
    header: "Nombres d'indicateurs",
    size: 160,
    options: {
      enableSorting: true,
      enableGrouping: true,
    },
  },
];
